import { useState, useEffect, useRef } from "react";
import { X, Trophy, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PR } from "@/types/evolve";

interface AddPRModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (lift: string, weight: number) => void;
  onUpdate?: (id: string, weight: number) => void;
  onDelete?: (id: string) => void;
  editingPR?: PR | null;
  preselectedLift?: string | null;
}

const COMMON_LIFTS = ["Bench Press", "Squat", "Deadlift", "Overhead Press", "Barbell Row"];

export function AddPRModal({ isOpen, onClose, onAdd, onUpdate, onDelete, editingPR, preselectedLift }: AddPRModalProps) {
  const [lift, setLift] = useState("");
  const [weight, setWeight] = useState("");
  const weightInputRef = useRef<HTMLInputElement>(null);
  const liftInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingPR) {
      setLift(editingPR.lift);
      setWeight(editingPR.weight.toString());
    } else if (preselectedLift) {
      setLift(preselectedLift);
      setWeight("");
    } else {
      setLift("");
      setWeight("");
    }
  }, [editingPR, preselectedLift]);

  // Auto-focus weight input when modal opens (after state is set)
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (editingPR || preselectedLift) {
          weightInputRef.current?.focus();
        } else {
          liftInputRef.current?.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, editingPR, preselectedLift]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (lift && weight) {
      if (editingPR && onUpdate) {
        onUpdate(editingPR.id, parseInt(weight));
      } else {
        onAdd(lift, parseInt(weight));
      }
      setLift("");
      setWeight("");
      onClose();
    }
  };

  const handleDelete = () => {
    if (editingPR && onDelete) {
      onDelete(editingPR.id);
      setLift("");
      setWeight("");
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg glass-card rounded-2xl p-6 space-y-6 animate-fade-in-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            {editingPR ? 'Edit PR' : 'Log New PR'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Lift</label>
            <input
              ref={liftInputRef}
              type="text"
              value={lift}
              onChange={(e) => setLift(e.target.value)}
              placeholder="Enter lift name"
              disabled={!!editingPR}
              className="w-full bg-secondary/50 border border-border/50 rounded-xl px-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
            />
            {!editingPR && (
              <div className="flex flex-wrap gap-2">
                {COMMON_LIFTS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLift(l)}
                    className="px-3 py-1 text-xs bg-secondary/50 border border-border/50 rounded-full hover:bg-secondary hover:border-primary/30 transition-all"
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Weight (lbs)</label>
            <input
              ref={weightInputRef}
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="Enter weight"
              className="w-full bg-secondary/50 border border-border/50 rounded-xl px-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
            />
          </div>

          <div className="flex gap-2">
            {editingPR && onDelete && (
              <Button 
                type="button" 
                variant="destructive" 
                className="flex-1"
                onClick={handleDelete}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            )}
            <Button type="submit" variant="evolve" className="flex-1" disabled={!lift || !weight}>
              {editingPR ? 'Update PR' : 'Save PR'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
